/**
 * Single H2H past fixture: date (left) + country flag + competition pill (right), then scoreboard row.
 */

import React, { useMemo } from 'react';
import { Text, View } from 'react-native';
import { SoccerKit } from '../../ui/SoccerKit';
import { CountryFlag } from '../../worldcup/CountryFlag/CountryFlag';
import { useTheme } from '../../../src/theme/useTheme';
import { getTeamKitConfig } from '../../../helpers/teamKit.helper';
import { SCOREBOARD_COMPLETED_LABEL } from '../../../src/gameResults/constants/gameResults.constants';
import type { MatchHubH2HMeeting } from '../../../src/match/types/matchHub.types';
import { formatMeetingDate } from './matchHubH2hFilters.helper';
import { getMatchHubH2HMeetingRowStyles } from './MatchHubH2HMeetingRow.styles';

const KIT_SIZE = 20;

export interface MatchHubH2HMeetingRowProps {
  meeting: MatchHubH2HMeeting;
  isLast: boolean;
}

export const MatchHubH2HMeetingRow: React.FC<MatchHubH2HMeetingRowProps> = ({ meeting: m, isLast }) => {
  const theme = useTheme();
  const styles = useMemo(() => getMatchHubH2HMeetingRowStyles(theme), [theme]);

  const homeKit = getTeamKitConfig(m.homeTeam);
  const awayKit = getTeamKitConfig(m.awayTeam);
  const scoreCenter = `${m.homeScore} - ${m.awayScore}`;
  const code = m.competitionCountryCode?.trim() ?? '';
  const pillLabel = m.competition.toUpperCase();

  return (
    <View style={[styles.meetingBlock, isLast && styles.meetingBlockLast]}>
      <View style={styles.topMetaRow}>
        <Text style={styles.dateLine}>{formatMeetingDate(m.date)}</Text>
        <View style={styles.competitionPill}>
          {code !== '' ? (
            <CountryFlag countryCode={code} size="compact" accessibilityLabel={`${m.competition} country`} />
          ) : null}
          <Text style={styles.competitionPillText} numberOfLines={1}>
            {pillLabel}
          </Text>
        </View>
      </View>

      <View style={styles.meetingScoreboardRow}>
        <View style={styles.nameColumnLeft}>
          <Text style={[styles.teamName, styles.teamNameTowardCenterLeft]} numberOfLines={2}>
            {m.homeTeam}
          </Text>
        </View>
        <View style={styles.kitCell}>
          <SoccerKit
            primary={homeKit.primary}
            secondary={homeKit.secondary}
            type={homeKit.type}
            size={KIT_SIZE}
          />
        </View>
        <View style={[styles.centerBlock, styles.centerBlockWithScore]}>
          <Text style={styles.centerStatus}>{SCOREBOARD_COMPLETED_LABEL}</Text>
          <Text style={styles.centerScore}>{scoreCenter}</Text>
        </View>
        <View style={styles.kitCell}>
          <SoccerKit
            primary={awayKit.primary}
            secondary={awayKit.secondary}
            type={awayKit.type}
            size={KIT_SIZE}
          />
        </View>
        <View style={styles.nameColumnRight}>
          <Text style={styles.teamName} numberOfLines={2}>
            {m.awayTeam}
          </Text>
        </View>
      </View>
    </View>
  );
};
